import { useState, useEffect, useCallback, type ComponentType } from "react"
import { SlidersHorizontal, ChevronDown, ChevronUp, Calendar, MapPin, DollarSign } from "lucide-react"
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet"
import { Button } from "@/components/ui/button"
import { MultiSelect } from "@/components/ui/multi-select"
import { cn } from "@/lib/utils"
import type { FilterGroupDefinition, FilterSelections } from "@/types/filters"

export interface FilterSheetProps {
	open: boolean
	onOpenChange: (open: boolean) => void
	title: string
	description?: string
	groups: FilterGroupDefinition[]
	selections: FilterSelections
	onApply: (selections: FilterSelections) => void
	onClearAll: () => void
	clearAllLabel: string
	applyLabel: string
	emptyGroupLabel?: string
	dateFromLabel?: string
	dateToLabel?: string
	selectPlaceholder?: string
	searchPlaceholder?: string
	noResultsLabel?: string
}

export function FilterSheet({
	open,
	onOpenChange,
	title,
	description,
	groups,
	selections,
	onApply,
	onClearAll,
	clearAllLabel,
	applyLabel,
	emptyGroupLabel,
	dateFromLabel = "From",
	dateToLabel = "To",
	selectPlaceholder = "Select...",
	searchPlaceholder = "Search...",
	noResultsLabel = "No results",
}: FilterSheetProps) {
	const [draft, setDraft] = useState<FilterSelections>({})
	const [expanded, setExpanded] = useState<Record<string, boolean>>({})

	useEffect(() => {
		if (open) {
			setDraft({ ...selections })
			const initialExpanded: Record<string, boolean> = {}
			for (const group of groups) {
				const vals = selections[group.key] ?? []
				if (vals.length > 0 && vals.some((v) => v !== "")) {
					initialExpanded[group.key] = true
				}
			}
			setExpanded(initialExpanded)
		}
	}, [open, selections, groups])

	const toggleExpanded = useCallback((key: string) => {
		setExpanded((prev) => ({ ...prev, [key]: !prev[key] }))
	}, [])

	const setRangeValue = useCallback((groupKey: string, index: 0 | 1, value: string) => {
		setDraft((prev) => {
			const current = prev[groupKey] ?? ["", ""]
			const next = [...current]
			next[index] = value
			return { ...prev, [groupKey]: next }
		})
	}, [])

	const setTextValue = useCallback((groupKey: string, value: string) => {
		setDraft((prev) => ({ ...prev, [groupKey]: [value] }))
	}, [])

	const handleApply = () => {
		onApply(draft)
		onOpenChange(false)
	}

	const handleClearAll = () => {
		const cleared: FilterSelections = {}
		for (const group of groups) {
			if (group.type === "date-range" || group.type === "number-range") {
				cleared[group.key] = ["", ""]
			} else if (group.type === "text") {
				cleared[group.key] = [""]
			} else {
				cleared[group.key] = []
			}
		}
		setDraft(cleared)
	}

	const draftTotal = Object.entries(draft).reduce((sum, [key, arr]) => {
		const group = groups.find((g) => g.key === key)
		if (group?.type === "date-range" || group?.type === "number-range") {
			return sum + (arr[0] || arr[1] ? 1 : 0)
		}
		if (group?.type === "text") {
			return sum + (arr[0] ? 1 : 0)
		}
		return sum + arr.length
	}, 0)

	const hasChanges = JSON.stringify(draft) !== JSON.stringify(selections)

	return (
		<Sheet open={open} onOpenChange={onOpenChange}>
			<SheetContent side="right" className="flex flex-col w-[400px] sm:max-w-[400px] p-0">
				{/* Header */}
				<SheetHeader className="px-6 pt-6 pb-4 flex-shrink-0">
					<SheetTitle className="flex items-center gap-2 pr-8">
						<SlidersHorizontal size={18} className="text-[#2D8A5E] flex-shrink-0" />
						{title}
						{draftTotal > 0 && (
							<span className="ml-1 bg-[#2D8A5E] text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
								{draftTotal}
							</span>
						)}
					</SheetTitle>
					{description && <SheetDescription>{description}</SheetDescription>}
				</SheetHeader>

				{/* Scrollable body */}
				<div className="flex-1 overflow-y-auto">
					{groups.map((group) => {
						const isExpanded = expanded[group.key] ?? false
						const draftSelected = draft[group.key] ?? []
						const GroupIcon = group.icon
						const isDateRange = group.type === "date-range"
						const isNumberRange = group.type === "number-range"
						const isText = group.type === "text"

						let activeCount = 0
						if (isDateRange || isNumberRange) {
							activeCount = draftSelected[0] || draftSelected[1] ? 1 : 0
						} else if (isText) {
							activeCount = draftSelected[0] ? 1 : 0
						} else {
							activeCount = draftSelected.length
						}

						return (
							<div key={group.key} className="border-b border-gray-100 last:border-b-0">
								{/* Collapsible group header */}
								<button
									type="button"
									onClick={() => toggleExpanded(group.key)}
									className="w-full flex items-center gap-3 px-6 py-4 hover:bg-gray-50 transition-colors text-left"
								>
									<GroupIcon size={16} className="text-gray-500 flex-shrink-0" />
									<span className="text-sm font-medium text-gray-700 flex-1">{group.label}</span>
									{activeCount > 0 && (
										<span className="text-xs text-[#178558] bg-[#2D8A5E]/10 px-2 py-0.5 rounded-full">
											{activeCount}
										</span>
									)}
									{isExpanded ? (
										<ChevronUp size={16} className="text-gray-400" />
									) : (
										<ChevronDown size={16} className="text-gray-400" />
									)}
								</button>

								{/* Expanded content */}
								{isExpanded && (
									<div className="px-6 pb-4">
										{isDateRange ? (
											<DateRangeInput
												from={draftSelected[0] ?? ""}
												to={draftSelected[1] ?? ""}
												onFromChange={(v) => setRangeValue(group.key, 0, v)}
												onToChange={(v) => setRangeValue(group.key, 1, v)}
												fromLabel={dateFromLabel}
												toLabel={dateToLabel}
											/>
										) : isNumberRange ? (
											<NumberRangeInput
												min={draftSelected[0] ?? ""}
												max={draftSelected[1] ?? ""}
												onMinChange={(v) => setRangeValue(group.key, 0, v)}
												onMaxChange={(v) => setRangeValue(group.key, 1, v)}
												minLabel={group.numberRangeLabels?.min ?? "Min"}
												maxLabel={group.numberRangeLabels?.max ?? "Max"}
												minPlaceholder={group.numberRangeLabels?.minPlaceholder ?? "0"}
												maxPlaceholder={group.numberRangeLabels?.maxPlaceholder ?? "\u221E"}
												icon={group.icon}
											/>
										) : isText ? (
											<TextInput
												value={draftSelected[0] ?? ""}
												onChange={(v) => setTextValue(group.key, v)}
												placeholder={group.placeholder}
												icon={group.icon}
											/>
										) : group.options.length === 0 ? (
											<p className="text-sm text-gray-400 italic">
												{emptyGroupLabel ?? "No options available"}
											</p>
										) : (
											<MultiSelect
												options={group.options}
												selected={draftSelected}
												onChange={(vals) => setDraft((prev) => ({ ...prev, [group.key]: vals }))}
												placeholder={selectPlaceholder}
												searchPlaceholder={searchPlaceholder}
												emptyMessage={noResultsLabel}
											/>
										)}
									</div>
								)}
							</div>
						)
					})}
				</div>

				{/* Footer */}
				<div className="flex items-center justify-between px-6 py-4 flex-shrink-0">
					<Button
						variant="ghost"
						size="sm"
						onClick={() => {
							handleClearAll()
							onClearAll()
							onOpenChange(false)
						}}
						disabled={draftTotal === 0}
						className="text-gray-500 hover:text-gray-700"
					>
						{clearAllLabel}
					</Button>
					<Button
						size="sm"
						onClick={handleApply}
						className="bg-[#2D8A5E] hover:bg-[#178558] text-white"
						disabled={!hasChanges}
					>
						{applyLabel}
					</Button>
				</div>
			</SheetContent>
		</Sheet>
	)
}

// ---------------------------------------------------------------------------
// Internal sub-components
// ---------------------------------------------------------------------------

function DateRangeInput({
	from,
	to,
	onFromChange,
	onToChange,
	fromLabel,
	toLabel,
}: {
	from: string
	to: string
	onFromChange: (v: string) => void
	onToChange: (v: string) => void
	fromLabel: string
	toLabel: string
}) {
	return (
		<div className="space-y-3 pl-7">
			<div className="flex items-center gap-3">
				<Calendar size={14} className="text-gray-400 flex-shrink-0" />
				<label className="text-xs text-gray-500 w-12 flex-shrink-0">{fromLabel}</label>
				<input
					type="date"
					value={from}
					onChange={(e) => onFromChange(e.target.value)}
					max={to || undefined}
					className="flex-1 text-sm border border-gray-200 rounded-lg px-3 py-1.5 focus:ring-2 focus:ring-[#2D8A5E]/30 focus:border-[#2D8A5E] outline-none"
				/>
			</div>
			<div className="flex items-center gap-3">
				<Calendar size={14} className="text-gray-400 flex-shrink-0" />
				<label className="text-xs text-gray-500 w-12 flex-shrink-0">{toLabel}</label>
				<input
					type="date"
					value={to}
					onChange={(e) => onToChange(e.target.value)}
					min={from || undefined}
					className="flex-1 text-sm border border-gray-200 rounded-lg px-3 py-1.5 focus:ring-2 focus:ring-[#2D8A5E]/30 focus:border-[#2D8A5E] outline-none"
				/>
			</div>
		</div>
	)
}

function NumberRangeInput({
	min,
	max,
	onMinChange,
	onMaxChange,
	minLabel,
	maxLabel,
	minPlaceholder,
	maxPlaceholder,
	icon: Icon,
}: {
	min: string
	max: string
	onMinChange: (v: string) => void
	onMaxChange: (v: string) => void
	minLabel: string
	maxLabel: string
	minPlaceholder: string
	maxPlaceholder: string
	icon?: ComponentType<{ size?: number; className?: string }>
}) {
	const IconComponent = Icon ?? DollarSign
	return (
		<div className="space-y-3 pl-7">
			<div className="flex items-center gap-3">
				<IconComponent size={14} className="text-gray-400 flex-shrink-0" />
				<label className="text-xs text-gray-500 w-12 flex-shrink-0">{minLabel}</label>
				<input
					type="number"
					value={min}
					onChange={(e) => onMinChange(e.target.value)}
					placeholder={minPlaceholder}
					className="flex-1 text-sm border border-gray-200 rounded-lg px-3 py-1.5 focus:ring-2 focus:ring-[#2D8A5E]/30 focus:border-[#2D8A5E] outline-none"
				/>
			</div>
			<div className="flex items-center gap-3">
				<IconComponent size={14} className="text-gray-400 flex-shrink-0" />
				<label className="text-xs text-gray-500 w-12 flex-shrink-0">{maxLabel}</label>
				<input
					type="number"
					value={max}
					onChange={(e) => onMaxChange(e.target.value)}
					placeholder={maxPlaceholder}
					className="flex-1 text-sm border border-gray-200 rounded-lg px-3 py-1.5 focus:ring-2 focus:ring-[#2D8A5E]/30 focus:border-[#2D8A5E] outline-none"
				/>
			</div>
		</div>
	)
}

function TextInput({
	value,
	onChange,
	placeholder,
	icon: Icon,
}: {
	value: string
	onChange: (v: string) => void
	placeholder?: string
	icon?: ComponentType<{ size?: number; className?: string }>
}) {
	return (
		<div className="pl-7">
			<div className="flex items-center gap-3">
				{Icon && <Icon size={14} className="text-gray-400 flex-shrink-0" />}
				<input
					type="text"
					value={value}
					onChange={(e) => onChange(e.target.value)}
					placeholder={placeholder}
					className="flex-1 text-sm border border-gray-200 rounded-lg px-3 py-1.5 focus:ring-2 focus:ring-[#2D8A5E]/30 focus:border-[#2D8A5E] outline-none"
				/>
			</div>
		</div>
	)
}

// ---------------------------------------------------------------------------
// FilterTriggerButton
// ---------------------------------------------------------------------------

export interface FilterTriggerButtonProps {
	activeCount: number
	label: string
	onClick: () => void
	className?: string
}

export function FilterTriggerButton({ activeCount, label, onClick, className }: FilterTriggerButtonProps) {
	return (
		<Button variant="outline" onClick={onClick} className={cn("flex items-center gap-2", className)}>
			<SlidersHorizontal size={16} />
			{label}
			{activeCount > 0 && (
				<span className="bg-[#2D8A5E] text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
					{activeCount}
				</span>
			)}
		</Button>
	)
}
